import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MousePointerClick, Users, PhoneIncoming, Inbox, MapPin, Globe, Smartphone, FileText, PlugZap } from 'lucide-react';
import { adminApi, apiError, cleanParams } from '../../lib/adminApi';
import { DateRangeFilter, rangeFor } from '../../components/admin/DateRangeFilter';
import { StatCard, Panel, BarList, TrendChart, Change, RefreshedAt, fmtDateTime } from '../../components/admin/AdminUI';

const ConfirmedCalls = ({ info }) => (
  <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-5" data-testid="confirmed-calls-card">
    <div className="flex items-start gap-3">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-400"><PhoneIncoming className="h-5 w-5" /></div>
      <div>
        <p className="font-oswald text-3xl leading-none text-slate-300" data-testid="confirmed-calls-value">—</p>
        <p className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Confirmed inbound calls</p>
        <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700" data-testid="call-tracking-status">
          <PlugZap className="h-3 w-3" />{info?.connected ? `Connected · refreshed ${fmtDateTime(info.last_refreshed)}` : 'Call tracking not connected'}
        </p>
      </div>
    </div>
  </div>
);

const RecentClicks = ({ items }) => (
  <Panel title="Recent phone taps" testId="recent-call-clicks">
    {!items?.length ? <p className="py-3 text-sm italic text-slate-400">No phone taps in this period</p> : (
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
            <tr><th className="py-2 pr-4">When</th><th className="py-2 pr-4">Page</th><th className="py-2 pr-4">Button</th><th className="py-2 pr-4">Source</th><th className="py-2">Device</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((c) => (
              <tr key={c.id} data-testid="call-click-row">
                <td className="whitespace-nowrap py-2 pr-4 text-slate-600">{fmtDateTime(c.at)}</td>
                <td className="py-2 pr-4">{c.page}</td>
                <td className="py-2 pr-4 text-slate-600">{c.placement}</td>
                <td className="py-2 pr-4 text-slate-600">{c.channel}</td>
                <td className="py-2 text-slate-600">{c.device}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Panel>
);

export default function AdminCalls() {
  const [date, setDate] = useState(() => { const [start, end] = rangeFor('30d'); return { preset: '30d', start, end }; });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await adminApi.get('/admin/calls', { params: cleanParams({ start: date.start, end: date.end }) });
      setData(d);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);
  const t = data?.totals;

  return (
    <div className="space-y-6" data-testid="admin-calls-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-oswald text-3xl uppercase text-[#0E1F3D] sm:text-4xl">Calls</h1>
          <p className="mt-1 text-sm text-slate-500">Taps on the phone number across the website. A tap is not the same as a completed call.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeFilter value={date} onChange={setDate} />
          <RefreshedAt at={data?.generated_at} onRefresh={load} loading={loading} testId="calls-refresh-btn" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Phone taps (clicks-to-call)" value={t?.clicks} Icon={MousePointerClick} accent="text-[#F58220] bg-[#F58220]/10" testId="calls-clicks" sub={<Change pct={t?.clicks_change_pct} testId="calls-clicks-change" />} />
        <StatCard label="Unique visitors who tapped" value={t?.unique_clickers} Icon={Users} testId="calls-unique" sub={<Change pct={t?.unique_change_pct} testId="calls-unique-change" />} />
        <ConfirmedCalls info={data?.confirmed_calls} />
        <StatCard label="Unique form leads (same period)" value={t?.form_leads} Icon={Inbox} accent="text-emerald-600 bg-emerald-50" testId="calls-form-leads" />
      </div>

      <Panel title="Phone taps per day" testId="calls-trend"><TrendChart data={data?.daily || []} color="#F58220" testId="calls-trend-chart" /></Panel>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Panel title="By page" Icon={FileText} testId="calls-by-page"><BarList items={data?.by_page} testId="calls-by-page-list" /></Panel>
        <Panel title="By button location" Icon={MapPin} testId="calls-by-placement"><BarList items={data?.by_placement} testId="calls-by-placement-list" /></Panel>
        <Panel title="By source" Icon={Globe} testId="calls-by-channel"><BarList items={data?.by_channel} testId="calls-by-channel-list" /></Panel>
        <Panel title="By device" Icon={Smartphone} testId="calls-by-device"><BarList items={data?.by_device} testId="calls-by-device-list" /></Panel>
      </div>

      <RecentClicks items={data?.recent} />
      <p className="text-xs text-slate-500">Phone taps are counted when a visitor taps a phone link on the website. No personal information is collected. To see real answered calls, connect a call-tracking service such as CallRail.</p>
    </div>
  );
}
